import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useIntl } from 'react-intl';
import { TEMPLATE_COLUMNS, autoAssign } from '../../utils/exerciseTemplate';

/**
 * (#27) Matcheo de columnas — orientado a las columnas del archivo del coach.
 *
 * Detectamos las columnas del Excel/CSV (ej. "Nombre", "Mi Video", "Col Z") y
 * mostramos UNA FILA POR COLUMNA, cada una con un selector "esta columna = qué
 * campo de nuestro modelo". Las columnas cuyo nombre coincide con un campo
 * conocido se auto-asocian; el coach resuelve a mano las de nombre arbitrario.
 *
 * El resultado se envía al backend como `{ campoCanonico: 'Header del archivo' }`,
 * que remapea las filas antes de procesarlas.
 */

/**
 * Los campos y su auto-mapeo viven en `utils/exerciseTemplate`, junto a los
 * encabezados de la plantilla. Estaban duplicados acá: si alguien cambiaba el
 * encabezado del Excel sin tocar los alias de este archivo, el archivo que
 * genera la propia app dejaba de auto-mapearse y nadie se enteraba hasta que un
 * coach abría el diálogo con catorce columnas sin asociar.
 */
const FIELDS = TEMPLATE_COLUMNS;

interface Props {
  visible: boolean;
  columns: string[];
  onConfirm: (mapping: Record<string, string>) => void;
  onHide: () => void;
}

export default function ColumnMappingDialog({ visible, columns, onConfirm, onHide }: Props) {
  const intl = useIntl();
  // assignments: columna del archivo -> campo del modelo ('' = no importar)
  const [assignments, setAssignments] = useState<Record<string, string>>({});

  // Auto-asociar cada vez que cambian las columnas detectadas.
  useEffect(() => {
    if (visible && columns.length) setAssignments(autoAssign(columns));
  }, [visible, columns]);

  const ignoreLabel = intl.formatMessage({ id: 'coach.importExercises.map.none' });

  const nameAssigned = Object.values(assignments).includes('name');
  const autoCount = Object.values(assignments).filter(Boolean).length;

  const handleConfirm = () => {
    // Invertimos a { campo: columna }. Si dos columnas eligen el mismo campo,
    // gana la primera (el resto se ignora).
    const mapping: Record<string, string> = {};
    columns.forEach((col) => {
      const field = assignments[col];
      if (field && !mapping[field]) mapping[field] = col;
    });
    onConfirm(mapping);
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label={intl.formatMessage({ id: 'common.cancel' })} className="p-button-text" onClick={onHide} />
      <Button
        label={intl.formatMessage({ id: 'coach.importExercises.map.confirm' })}
        icon="pi pi-arrow-right"
        onClick={handleConfirm}
        disabled={!nameAssigned}
      />
    </div>
  );

  return (
    <Dialog
      header={intl.formatMessage({ id: 'coach.importExercises.map.title' })}
      visible={visible}
      onHide={onHide}
      draggable={false}
      resizable={false}
      dismissableMask
      footer={footer}
      style={{ width: '46rem', maxWidth: '94vw' }}
    >
      <div className="flex flex-column gap-3">
        <p className="m-0" style={{ color: 'var(--ios-text-secondary)' }}>
          {intl.formatMessage({ id: 'coach.importExercises.map.intro' })}
        </p>
        <span style={{ fontSize: '0.78rem', color: 'var(--ios-text-tertiary)' }}>
          {intl.formatMessage({ id: 'coach.importExercises.map.detectedColumns' }, { n: columns.length })}
          {autoCount > 0 &&
            ` · ${intl.formatMessage({ id: 'coach.importExercises.map.autoMatched' }, { n: autoCount })}`}
        </span>

        <div className="flex flex-column gap-2" style={{ maxHeight: '54vh', overflowY: 'auto' }}>
          {/* Encabezado de la tabla columna -> campo */}
          <div
            className="flex align-items-center gap-3"
            style={{
              fontSize: '0.72rem',
              color: 'var(--ios-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }}
          >
            <span style={{ flex: '1 1 45%' }}>
              {intl.formatMessage({ id: 'coach.importExercises.map.fileColumn' })}
            </span>
            <span style={{ flex: '1 1 55%' }}>
              {intl.formatMessage({ id: 'coach.importExercises.map.modelField' })}
            </span>
          </div>

          {columns.map((col) => {
            // Deshabilitar campos ya tomados por OTRA columna (un campo = una columna).
            const takenByOthers = new Set(
              columns
                .filter((c) => c !== col)
                .map((c) => assignments[c])
                .filter(Boolean)
            );
            const fieldOptions = [
              { label: ignoreLabel, value: '', disabled: false },
              ...FIELDS.map((f) => ({
                label: intl.formatMessage({ id: f.headerId }) + (f.required ? ' *' : ''),
                value: f.key,
                disabled: takenByOthers.has(f.key)
              }))
            ];
            return (
              <div
                key={col}
                className="flex align-items-center gap-3"
                style={{ borderBottom: '1px solid var(--ios-divider)', padding: '0.4rem 0' }}
              >
                <span
                  title={col}
                  style={{
                    flex: '1 1 45%',
                    minWidth: 0,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    fontFamily: 'ui-monospace, monospace',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {col}
                </span>
                <Dropdown
                  value={assignments[col] ?? ''}
                  options={fieldOptions}
                  optionDisabled="disabled"
                  onChange={(e) => setAssignments((prev) => ({ ...prev, [col]: e.value }))}
                  placeholder={ignoreLabel}
                  style={{ flex: '1 1 55%', minWidth: 0 }}
                  className="w-full"
                />
              </div>
            );
          })}
        </div>

        {!nameAssigned && (
          <small style={{ color: '#f59e0b' }}>{intl.formatMessage({ id: 'coach.importExercises.map.required' })}</small>
        )}
      </div>
    </Dialog>
  );
}
